import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import Papa from 'papaparse';
import './App.css';
import { InteractionStatus } from "@azure/msal-browser";
import { v4 as uuidv4 } from 'uuid';
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const App = () => {
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [sessionId, setSessionId] = useState(null);

  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    if (accounts.length === 0 && inProgress === InteractionStatus.None) {
      handleLogin();
    }
  }, [accounts, inProgress]);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  };

  const onDrop = (acceptedFiles) => {
    const formData = new FormData();
    acceptedFiles.forEach((file) => {
      formData.append('files[]', file);
    });

    const uniqueSessionId = uuidv4(); 
    formData.append("session_id", uniqueSessionId);

    setLoading(true);
    setDownloadUrl(null); 
    setCsvData(null); 
    setProgress(0); 
    setSessionId(uniqueSessionId);

    // Make the upload request
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })
    .then((resp) => {
      // Trigger the download
      console.log('Session ID',uniqueSessionId);
      axios.post(`${process.env.REACT_APP_API_BASE_URL}/download`, { session_id: uniqueSessionId }, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          setDownloadUrl(url);

          // Parse the CSV file
          Papa.parse(response.data, {
            complete: (result) => {
              setCsvData(result.data);
            },
            header: true,
            skipEmptyLines: true,
          });

          setLoading(false);
          setSessionId(null)
        })
        .catch((error) => {
          console.error('Error downloading file:', error);
          alert('Error downloading file');
          setLoading(false);
          setSessionId(null)
        });
    })
    .catch((error) => {
      console.error('Error uploading files:', error);
      alert('Error uploading files');
      setLoading(false);
    });


    // Small delay before establishing SSE connection
    console.log('Establishing SSE connection');
  };

  useEffect(() => {
    if (!sessionId) return;

    const eventSource = new EventSource(`${process.env.REACT_APP_API_BASE_URL}/stream?channel=progress-${sessionId}`, {
      withCredentials: true,
    });
    console.log('SSE connection established');

    eventSource.onmessage = (event) => {
      console.log('Received data');
      const data = JSON.parse(event.data);
      if (data.type === 'progress') {
        setProgress(data.progress);
      } else if (data.type === 'status' && data.status === 'completed') {
        eventSource.close();
        console.log('SSE connection closed');
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource failed:', error);
      eventSource.close();
    };

    return () => {
      eventSource.close();
      console.log('SSE connection closed');
    };
  }, [sessionId]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: '.pdf',
    multiple: true,
  });

  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-grow py-10 bg-gray-100">
        <AuthenticatedTemplate>
          <h1 class="text-4xl font-bold">Tax Withholding Certificates</h1>
          <div {...getRootProps()} className="dropzone">
            <input {...getInputProps()} />
            <p>Drag 'n' drop PDF files here, or click to select files</p>
          </div>
          {loading && (
            <div>
              <p>Processing your files</p>
              <div className="flex justify-center">
                <div className="w-48 h-2 bg-gray-200 rounded overflow-hidden">
                  <div className="h-full bg-green-500 animate-[progressMove_1.5s_linear_infinite]"></div>
                </div>
              </div>
            </div>
          )}
          {downloadUrl && (
            <div className="download-section">
              <button onClick={() => {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', 'output.csv'); 
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link); 
              }}>
                Download
              </button>
            </div>
          )}
          {csvData && (
            <div className="csv-table px-4 sm:px-8">
            <div className="overflow-x-auto shadow-md rounded-lg">
              <table className="min-w-full bg-white border-collapse border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {Object.keys(csvData[0]).map((header) => (
                      <th
                        key={header}
                        className="px-6 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`${rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
                    >
                      {Object.values(row).map((value, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-6 py-4 text-sm text-gray-700 border-b border-gray-200"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <p>You are not logged in.</p>
        </UnauthenticatedTemplate>
      </div>
      <Footer />
    </div>
  );
};

export default App;